var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext } from 'react';
import { usePopper } from 'react-popper';
import { FocusTrap } from '../../utilities/FocusTrap';
import { OverlayContext, calculateZindex, } from '../../utilities/OverlayContext';
import { Portal } from '../Portal';
import classnames from 'classnames/dedupe';
import { useMergedRef } from '../../hooks/useMergedRef';
import { AnimatePresence, motion } from 'framer-motion';
export const Popper = React.forwardRef((_a, ref) => {
    var _b;
    var { autoFlipHorizontally = false, autoFlipVertically = false, el: PopperElement = 'div', width = 'auto', direction = 't', portal = false, children, className, onClickOutside, open, style = {}, trigger, bodyClassName, caret = { show: false }, animation, offset = { skidding: 0, distance: 0 }, focusTrapOptions } = _a, props = __rest(_a, ["autoFlipHorizontally", "autoFlipVertically", "el", "width", "direction", "portal", "children", "className", "onClickOutside", "open", "style", "trigger", "bodyClassName", "caret", "animation", "offset", "focusTrapOptions"]);
    const [referenceElement, setReferenceElement] = React.useState(null);
    const [popperElement, setPopperElement] = React.useState(null);
    const localRef = React.useRef(null);
    const popperRef = useMergedRef(ref, localRef);
    const [arrowElement, setArrowElement] = React.useState(null);
    const [scrollPosition, setScrollPosition] = React.useState(0);
    const context = useContext(OverlayContext);
    const [focusWrapper, setFocusWrapper] = React.useState(1);
    const widthObserver = React.useMemo(() => ({
        name: 'widthObserver',
        enabled: width !== '100',
        phase: 'beforeWrite',
        requires: ['computeStyles'],
        fn: ({ state }) => {
            if (typeof width === 'string') {
                state.styles.popper.width = `${width}px`;
            }
            state.styles.popper.width = width;
        },
        effect: ({ instance }) => {
            instance.forceUpdate();
        },
    }), [width]);
    const sameWidth = React.useMemo(() => ({
        name: 'sameWidth',
        enabled: width === '100',
        phase: 'beforeWrite',
        requires: ['computeStyles'],
        fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        effect: ({ state }) => {
            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
        },
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [width, (_b = popperRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width]);
    const observeReferenceModifier = React.useMemo(() => ({
        name: 'observeReferenceModifier',
        enabled: true,
        phase: 'main',
        fn: () => { },
        effect: ({ state, instance }) => {
            const RO_PROP = '__popperjsRO__';
            const { reference } = state.elements;
            reference[RO_PROP] = new ResizeObserver(() => {
                instance.update();
            });
            reference[RO_PROP].observe(reference);
            return () => {
                reference[RO_PROP].disconnect();
                delete reference[RO_PROP];
            };
        },
    }), []);
    const centerPlacement = React.useMemo(() => ({
        name: 'offset',
        enabled: true,
        options: {
            offset: ({ reference, popper }) => {
                var _a, _b;
                if (direction === 'c') {
                    const offsetY = -(reference.height / 2) + -(popper.height / 2);
                    return [0, offsetY];
                }
                if (caret.show) {
                    return [0, 8];
                }
                return [(_a = offset.skidding) !== null && _a !== void 0 ? _a : 0, (_b = offset.distance) !== null && _b !== void 0 ? _b : 4];
            },
        },
    }), [direction, caret.show, offset.skidding, offset.distance]);
    const directionMapping = () => {
        switch (direction) {
            case 't':
                return 'top';
            case 'tr':
                return 'top-start';
            case 'tl':
                return 'top-end';
            case 'r':
                return 'right';
            case 'rt':
                return 'right-end';
            case 'rb':
                return 'right-start';
            case 'b':
            case 'c':
                return 'bottom';
            case 'br':
                return 'bottom-start';
            case 'bl':
                return 'bottom-end';
            case 'l':
                return 'left';
            case 'lt':
                return 'left-end';
            case 'lb':
                return 'left-start';
            default:
                return 'bottom-end';
        }
    };
    const fallbackPlacements = () => {
        if (autoFlipHorizontally) {
            switch (direction) {
                case 'r':
                    return ['left'];
                case 'rb':
                    return ['left-start'];
                case 'rt':
                    return ['left-end'];
                case 'l':
                    return ['right'];
                case 'lb':
                    return ['right-start'];
                case 'lt':
                    return ['right-end'];
                default:
                    break;
            }
        }
        if (autoFlipVertically) {
            switch (direction) {
                case 't':
                    return ['bottom'];
                case 'tr':
                    return ['bottom-start'];
                case 'tl':
                    return ['bottom-end'];
                case 'b':
                    return ['top'];
                case 'br':
                    return ['top-start'];
                case 'bl':
                    return ['top-end'];
                default:
                    break;
            }
        }
        return [];
    };
    const { styles, attributes, forceUpdate } = usePopper(referenceElement, popperElement, {
        modifiers: [
            centerPlacement,
            widthObserver,
            sameWidth,
            observeReferenceModifier,
            {
                name: 'flip',
                enabled: (autoFlipHorizontally || autoFlipVertically) &&
                    direction !== 'c',
                options: {
                    fallbackPlacements: fallbackPlacements(),
                },
            },
            {
                name: 'arrow',
                enabled: caret.show,
                options: { element: arrowElement, padding: 8 },
            },
        ],
        placement: directionMapping(),
    });
    if (typeof window !== 'undefined' &&
        window.scrollY !== 0 &&
        scrollPosition === 0)
        setScrollPosition(window.scrollY);
    React.useEffect(() => {
        scrollPosition !== 0 && window.scrollTo(0, scrollPosition);
    }, [scrollPosition]);
    React.useEffect(() => {
        forceUpdate;
    }, [forceUpdate, direction]);
    React.useEffect(() => {
        function handleClickOutside(event) {
            var _a, _b;
            if (!open)
                return;
            //  Target is not part of wrapper or element
            const target = (_b = (_a = event.composedPath) === null || _a === void 0 ? void 0 : _a.call(event)[0]) !== null && _b !== void 0 ? _b : event.target;
            if ((popperRef === null || popperRef === void 0 ? void 0 : popperRef.current.contains(target)) ||
                (popperElement === null || popperElement === void 0 ? void 0 : popperElement.contains(target))) {
                return;
            }
            onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside();
        }
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, onClickOutside, popperElement, popperRef]);
    const animations = Object.assign({ initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: {
            type: 'easeInOut',
            duration: 0.3,
        } }, animation);
    React.useEffect(() => {
        // Removes wrapper to be focusable element after initial tab
        function eventHandler(event) {
            event.key === 'Tab' && setFocusWrapper(-1);
        }
        document.addEventListener('keydown', eventHandler);
        return () => document.removeEventListener('keydown', eventHandler);
    }, []);
    return (React.createElement(PopperElement, Object.assign({ ref: popperRef, className: classnames(className), style: Object.assign({}, style) }, props),
        React.createElement("div", { style: {
                display: PopperElement === 'span' && 'inline-block',
            }, ref: setReferenceElement }, trigger),
        React.createElement(Portal, { conditional: portal },
            React.createElement(OverlayContext.Provider, { value: context.concat('Popper') }, open && (React.createElement(FocusTrap, Object.assign({}, focusTrapOptions),
                React.createElement("div", Object.assign({ ref: setPopperElement, className: "Popper", style: Object.assign(Object.assign({}, styles.popper), { zIndex: calculateZindex(context) + 1 }), tabIndex: focusWrapper }, attributes.popper),
                    React.createElement(AnimatePresence, null,
                        React.createElement(motion.div, Object.assign({}, animations, { className: classnames('Popper__body', bodyClassName) }),
                            children,
                            caret.show && (React.createElement("div", Object.assign({ ref: setArrowElement, className: classnames('Popper__caret', caret.className), style: styles.arrow }, attributes.popper))))))))))));
});
//# sourceMappingURL=Popper.js.map